import { Core } from "@faire/design-tokens";
import { useRedirectAfterSignIn } from "@faire/retailer-visitor-shared/components/AppSignIn/useRedirectAfterSignIn";
import { AppSignUpForm } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpForm";
import {
  getModalImageryFromPageResults,
  getModalImageryType,
  isAutoSignUpVariant,
} from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpModalVariant";
import { useAppSignUpPageResults } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpPageResultsProvider";
import { Disclaimer } from "@faire/retailer-visitor-shared/components/AppSignUp/components/Disclaimer";
import { ImageOrLogo } from "@faire/retailer-visitor-shared/components/AppSignUp/components/ImageOrLogo";
import { MaybeFaireDirectBrandBannerImage } from "@faire/retailer-visitor-shared/components/AppSignUp/components/MaybeFaireDirectBrandBannerImage";
import { FaireDirectSubtitle } from "@faire/retailer-visitor-shared/components/AppSignUp/components/Subtitle";
import { Title } from "@faire/retailer-visitor-shared/components/AppSignUp/components/Title";
import {
  trackFDSignupModal,
  trackSignupModal,
} from "@faire/retailer-visitor-shared/components/AppSignUp/eventTracking";
import {
  LongSignInLink,
  ShortSignInLink,
} from "@faire/retailer-visitor-shared/components/AppSignUp/SignInInformation";
import { useAppSignUpState } from "@faire/retailer-visitor-shared/components/AppSignUp/useAppSignUpState";
import { SocialSignOnButtons } from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/index";
import { signUpCache } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { getIsBrandPreview } from "@faire/retailer-visitor-shared/lib/getIsBrandPreview";
import { showSSOButtons } from "@faire/retailer-visitor-shared/lib/ssoUtils";
import {
  useAssignSettingBatch8SignUpModalChanges,
  useSettingBatch8SignUpModalChanges,
} from "@faire/retailer-visitor-shared/settings/useSettingBatch8SignUpModalChanges";
import { useSSOErrorStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOErrorStore";
import { useSSOMFAStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOMFAStore";
import { useSSOPasswordStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOPasswordStore";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { useScrollStore } from "@faire/web--source/common/ScrollStore";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { Modal } from "@faire/web--source/slate/Modal/Modal";
import {
  getSpacing,
  GrowingSpacer,
  Spacer,
} from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackLoggedOutRetailersAutoSignupModalView } from "@faire/web-api--source/events/loggedOutRetailers/view/autoSignupModal";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

export const AppSignUp: React.FC = () => {
  /**
   * Important: it's mandatory to NOT use viewport inside this component (or any of its children).
   * Responsiveness should be handled with css media queries only.
   * Please refer to FD-37046 for more information
   */

  const { isMobile } = useViewport();
  const location = useLocation();

  const {
    email,
    signUpParams,
    dismissible,
    modalVariant,
    isFaireDirectSignUp,
    handleModalClose,
    handleSubmit,
    setEmailValidationErrors,
    markUserEntersEmail,
  } = useAppSignUpState();

  const { errorMessage } = useSSOErrorStore();
  const { userClickedSSOButton } = useSSOStore();

  const assignSettingBatch8SignUpModalChanges =
    useAssignSettingBatch8SignUpModalChanges();

  const { popNoScroll, pushNoScroll } = useScrollStore([
    "popNoScroll",
    "pushNoScroll",
  ]);

  const reactivePageResults = useAppSignUpPageResults();
  const [pageResults, setPageResults] = useState(reactivePageResults);
  useEffect(() => {
    // When the page unmounts, data in reactivePageResults will become undefined.
    // To avoid the featuredImage changing unexpectedly and jarring the user,
    // we keep the previous data from pageResults state and only override it
    // if the new data is not undefined.
    setPageResults((currentPageResults) => ({
      ...currentPageResults,
      ...reactivePageResults,
    }));
  }, [reactivePageResults]);

  const defaultTheme = useTheme();

  useEffect(() => {
    // we want a fresh sign up request when user lands on the homepage
    signUpCache.clear();
  }, []);

  const modalImageryType = getModalImageryType(modalVariant);
  const settingBatch8SignUpModalChanges = useSettingBatch8SignUpModalChanges();

  const productToken = signUpParams.product || signUpParams.refP;

  const featuredImage = getModalImageryFromPageResults(
    modalImageryType,
    pageResults,
    productToken
  );

  useEffect(() => {
    if (isFaireDirectSignUp) {
      trackFDSignupModal(location.pathname);
    } else {
      trackSignupModal(signUpParams.product, location.pathname);
      assignSettingBatch8SignUpModalChanges();
    }
  }, [
    isFaireDirectSignUp,
    location.pathname,
    signUpParams.product,
    assignSettingBatch8SignUpModalChanges,
  ]);

  useEffect(() => {
    pushNoScroll();
  }, [pushNoScroll]);

  useEffect(() => {
    return () => {
      popNoScroll();
    };
  }, [popNoScroll]);

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "modal-sign-up",
    pageLoadKey: "static",
    variant: modalVariant,
    isPageLoading: false,
    numComponents: 1,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    enableTrackPerformanceBreakdown: "internal-tracking",
  });

  const isBrandPreview = getIsBrandPreview();

  const viewRef = useDoActionWhenVisible(() => {
    if (isAutoSignUpVariant(modalVariant)) {
      trackLoggedOutRetailersAutoSignupModalView(location.pathname);
    }
  });

  const shouldSignInBeAtBottom =
    (isFaireDirectSignUp && !isBrandPreview) || settingBatch8SignUpModalChanges;

  const ssoButtonSection = showSSOButtons() ? (
    <SSOButtonsSection
      isBrandPreview={isBrandPreview}
      isFaireDirectSignUp={isFaireDirectSignUp}
    />
  ) : null;

  const textAlignOverride =
    isFaireDirectSignUp && isMobile ? "left" : undefined;

  return (
    <StyledModal
      setContentRef={loadTimingRef}
      onClose={dismissible ? handleModalClose : undefined}
      height="auto"
      padding="0"
      contentStyle={{
        minHeight: "600px",
        alignItems: "stretch",
        width: "500px",
      }}
      theme={defaultTheme}
      align="stretch"
      noPortal
      data-test-id="appSignUpModal"
      overlayStyle={{
        /* Use dvh units to prevent overlap with bottom bar on iOS Safari & iOS Chrome */
        height: "100dvh",
      }}
    >
      {isFaireDirectSignUp ? (
        <GrowingPictureWrapper data-test-id="faire-direct-brand-banner-image">
          <MaybeFaireDirectBrandBannerImage />
        </GrowingPictureWrapper>
      ) : (
        <GrowingSpacer />
      )}
      <StyledColumn
        ref={viewRef}
        align="stretch"
        $isFaireDirectSignUp={isFaireDirectSignUp}
      >
        <Column
          align={
            isFaireDirectSignUp
              ? { mobileAndAbove: "start", tabletAndAbove: "center" }
              : "center"
          }
          flex={1}
        >
          <ImageOrLogo
            logo={modalImageryType === "no-image"}
            image={featuredImage}
          />
          <Spacer height="2x" />
          <Title modalVariant={modalVariant} align={textAlignOverride} />
          <Spacer height="1x" />
          {isFaireDirectSignUp ? (
            <FaireDirectSubtitle align={textAlignOverride} />
          ) : null}
          {userClickedSSOButton && errorMessage ? (
            <>
              <Spacer height="2x" />
              <Typography
                color={Core.text.critical}
                role="alert"
                align="center"
              >
                {errorMessage}
              </Typography>
              <Spacer height="3x" />
            </>
          ) : (
            <Spacer height="2x" />
          )}
          <AppSignUpForm
            email={email}
            handleSubmit={handleSubmit}
            setEmailValidationErrors={setEmailValidationErrors}
            markUserEntersEmail={markUserEntersEmail}
            modalVariant={modalVariant}
          />
          {shouldSignInBeAtBottom ? null : <ShortSignInLink />}
          {ssoButtonSection}
          <SignInLinkSection
            shouldSignInBeAtBottom={!!shouldSignInBeAtBottom}
            shouldShowShortSignInLink={
              settingBatch8SignUpModalChanges && !isFaireDirectSignUp
            }
          />
        </Column>
        <GrowingSpacer />
        <Disclaimer
          isFaireDirectSignUp={isFaireDirectSignUp}
          textAlign="center"
        />
      </StyledColumn>
    </StyledModal>
  );
};

export const SSOButtonsSection: React.FC<{
  isBrandPreview: boolean;
  isFaireDirectSignUp: boolean;
}> = (props) => {
  const isSSR = useIsSSR();
  const location = useLocation();
  const { replace } = useHistory();
  const redirectAfterSignIn = useRedirectAfterSignIn();

  const { setMFADescription, setMFAOptions, setMFATitle } = useSSOMFAStore();

  const { setShowConfirmPasswordView } = useSSOPasswordStore();

  return (
    <SocialSignOnButtons
      disabled={props.isBrandPreview || isSSR}
      onSignIn={(user) => {
        redirectAfterSignIn(location, user);
      }}
      onSelectMFA={(mfaOptions) => {
        setMFAOptions(mfaOptions);
        replace(
          getCurrentPath().delete("signUp").append({ signIn: "1" }).toString()
        );
      }}
      onMFA={(mfaDescription, mfaTitle) => {
        setMFADescription(mfaDescription);
        setMFATitle(mfaTitle);
        replace(
          getCurrentPath().delete("signUp").append({ signIn: "1" }).toString()
        );
      }}
      onConfirmPassword={() => {
        setShowConfirmPasswordView(true);
        replace(
          getCurrentPath().delete("signUp").append({ signIn: "1" }).toString()
        );
      }}
      hideApple
      sourceId={`signup_modal${
        props.isFaireDirectSignUp ? "_faire_direct" : ""
      }`}
    />
  );
};

const SignInLinkSection: React.FC<{
  shouldSignInBeAtBottom: boolean;
  shouldShowShortSignInLink: boolean;
}> = ({ shouldSignInBeAtBottom, shouldShowShortSignInLink }) => {
  if (shouldSignInBeAtBottom) {
    if (shouldShowShortSignInLink) {
      return (
        <>
          <ShortSignInLink />
          <Spacer height="3x" />
        </>
      );
    } else {
      return <LongSignInLink />;
    }
  } else {
    return <Spacer height="3x" />;
  }
};

const StyledColumn = styled(Column)<{ $isFaireDirectSignUp: boolean }>`
  padding: ${({ $isFaireDirectSignUp }) =>
    $isFaireDirectSignUp ? getSpacing("3x", "5x", "5x") : getSpacing("5x")};
  flex: 1 1 auto;

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x")};
  }
`;

const StyledModal = styled(Modal)`
  ${responsiveMediaQueries.mobile} {
    /* These mobile !important rules are mandatory to be here.
    This is a workaround so the sign up modal
    correctly renders inside FB/IG embedded browsers.
    Please refer to FD-37046 for more information. */
    width: 100% !important;
    height: 100% !important;
  }

  /* the modal component has a min-height of 600px, when using mobile,
  devices in landscape form, a lot of the modal is cut off and not able
  to be scrolled to. */
  min-height: 0px !important;
`;

const GrowingPictureWrapper = styled(Flex)`
  flex-grow: 1;
  flex-shrink: 0;
`;
